import { createRouter, createWebHashHistory, START_LOCATION } from 'vue-router';
import LoginView from '@/components/LoginView.vue';
import PlatformView from '@/components/PlatformView.vue';
import CloudEditorView from '@/components/CloudEditorView.vue';
import { registerAppRouter, setEditorReturnRoute } from '@/services/navigation';
import { getAuthToken, isUserLoggedOut } from '@/utils/authToken';
import { mapJwtPayloadToAuthSession } from '@/utils/authMapper';
import { usePlatformStore } from '@/composables/usePlatformStore';
import { isUnitTestRuntime } from '@/utils/runtimeEnv';

const routes = [
  {
    path: '/',
    redirect: '/login',
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { title: '登录 · 鲸夕智绘' },
  },
  {
    path: '/platform',
    name: 'platform',
    component: PlatformView,
    redirect: '/platform/templates',
    children: [
      {
        path: ':tab',
        name: 'platform-tab',
        component: PlatformView,
        meta: { title: '指挥中心 · 鲸夕智绘' },
      },
    ],
  },
  {
    path: '/editor',
    name: 'editor',
    component: CloudEditorView,
    meta: { title: '矢量设计云编辑器 · 鲸夕智绘' },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/login',
  },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

registerAppRouter(router);

let pendingAutoLogin = null;

function hasWeComOAuthCallback() {
  if (typeof window === 'undefined') return false;
  const search = new URLSearchParams(window.location.search);
  if (search.get('code')) return true;
  const hash = window.location.hash || '';
  const qIndex = hash.indexOf('?');
  if (qIndex >= 0) {
    return Boolean(new URLSearchParams(hash.slice(qIndex + 1)).get('code'));
  }
  return false;
}

router.beforeEach(async (to, from) => {
  if (to.path === '/editor') {
    const queryFrom = to.query?.from || to.query?.returnUrl || to.query?.returnTab;
    if (queryFrom && typeof queryFrom === 'string') {
      const normalized = queryFrom.startsWith('/platform') ? queryFrom : `/platform/${queryFrom.replace(/^\//, '')}`;
      setEditorReturnRoute(normalized);
    } else if (from && from.path && from.path !== '/editor' && from.path !== '/login' && from.path !== '/') {
      setEditorReturnRoute(from.fullPath);
    }
  } else if (to.path.startsWith('/platform/')) {
    setEditorReturnRoute(to.fullPath);
  }

  if (isUnitTestRuntime()) return true;

  const isFirstNavigation = from === START_LOCATION;

  if (to.path !== '/login' && to.path !== '/') {
    const { session, loadOrganizationStores } = usePlatformStore();
    // 未登录不放进平台/设计器：接口会整片 401，页面只会显示空模板和各种加载报错。
    // 登录页守卫会用缓存 Token 免扫码登录，再转回平台。
    if (session.userId == null) return '/login';
    // 刷新页面时组织树需要重新拉取，否则店铺/部门列表停留在上次缓存
    if (isFirstNavigation) await loadOrganizationStores({ force: true });
    return true;
  }

  if (isUserLoggedOut()) return true;
  if (hasWeComOAuthCallback()) return true;

  const token = getAuthToken();
  if (!token) return true;

  const authProfile = mapJwtPayloadToAuthSession(token);
  if (!authProfile?.loginRole) return true;

  const { tryAutoLoginFromToken, session, loadOrganizationStores } = usePlatformStore();
  const sameUser = session.userId != null
    && String(session.userId) === String(authProfile.userId)
    && Array.isArray(session.backendRoles)
    && session.backendRoles.length > 0;
  if (sameUser) {
    if (isFirstNavigation) await loadOrganizationStores({ force: true });
    return '/platform/templates';
  }

  if (!pendingAutoLogin) {
    pendingAutoLogin = tryAutoLoginFromToken(token).finally(() => {
      pendingAutoLogin = null;
    });
  }
  const ok = await pendingAutoLogin;
  return ok ? '/platform/templates' : true;
});

router.afterEach((to) => {
  if (to.meta?.title) {
    document.title = to.meta.title;
  }
});

export default router;
